import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { loadProfile } from "../profileStore";
import api, { API_BASE } from "../api";
import Overview from "./tabs/Overview";
import DailyPlan from "./tabs/DailyPlan";
import WeeklyPlan from "./tabs/WeeklyPlan";
import Supplements from "./tabs/Supplements";
import Vitamins from "./tabs/Vitamins";
import Checkin from "./tabs/Checkin";
import Cravings from "./tabs/Cravings";
import FoodLog from "./tabs/FoodLog";

const TABS = ["overview", "daily", "weekly", "supplements", "vitamins", "checkin", "cravings", "foodlog"];

const Dashboard = () => {
  const { t, lang } = useI18n();
  const { serviceMode } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [calc, setCalc] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [weekOverrides, setWeekOverrides] = useState({});
  const [pdfLoading, setPdfLoading] = useState(false);
  const [codeStatus, setCodeStatus] = useState(null);

  useEffect(() => {
    const p = loadProfile();
    if (!p || !p.sport) {
      navigate("/onboarding");
      return;
    }
    setProfile(p);
    api.post("/calc/tdee", p).then((r) => setCalc(r.data));
    api.get("/auth/code-status").then((r) => setCodeStatus(r.data)).catch(() => {});
  }, [navigate]);

  // Centralised weekly fetch — Overview (Sumar) needs the 7-day average and
  // WeeklyPlan needs the per-day breakdown. Both share the same source of
  // truth so any day-type override propagates everywhere instantly.
  useEffect(() => {
    if (!profile) return;
    api.post("/plan/weekly", { profile, overrides: weekOverrides })
      .then((r) => setWeeklyData(r.data));
  }, [profile, weekOverrides]);

  const handleDayTypeChange = (dayKey, newType) => {
    setWeekOverrides((prev) => ({ ...prev, [dayKey]: newType }));
  };

  const startNewPlan = async () => {
    if (!window.confirm(t.dashboard.new_plan_confirm)) return;
    // Tell the server to drop the saved profile snapshot so F5 / cookie wipe
    // can no longer auto-restore. The next onboarding finalize will consume
    // another plan from the quota.
    try {
      await api.post("/plan/reset", { profile_type: profile?.profile_type || "pro" });
    } catch (e) {
      // ignore — local reset still proceeds
    }
    localStorage.removeItem("sne_profile");
    localStorage.removeItem("sne_food_log");
    navigate("/onboarding", { replace: true });
  };

  const downloadPDF = async () => {
    if (!profile) return;
    setPdfLoading(true);
    try {
      const res = await api.post("/export/pdf",
        { profile, language: lang },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "sports_nutrition_plan.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setPdfLoading(false);
    }
  };

  if (!profile || !calc) {
    return <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 text-white/60">{t.common.loading}</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs tracking-widest text-[#FF3B30]">// DASHBOARD</div>
          <h1 className="font-heading text-4xl md:text-5xl">
            {profile.sport.toUpperCase()} · {profile.position.replace(/_/g, " ").toUpperCase()}
          </h1>
          {profile.profile_type === "pro" && !serviceMode && (
            <div
              data-testid="locked-identity-badge"
              className="mt-3 inline-flex items-center gap-2 px-3 py-2 font-mono text-xs"
              style={{ background: "rgba(234,179,8,0.08)", border: "1px solid #eab308", color: "#eab308" }}
            >
              <span className="tracking-widest">🔒 {t.dashboard.locked_identity}:</span>
              <span className="text-white">
                {profile.name ? `${profile.name} · ` : ""}
                {profile.sex === "M" ? "M" : "F"} · {profile.age}y · {profile.sport} · {profile.position.replace(/_/g, " ")}
              </span>
            </div>
          )}
          {serviceMode && (
            <div
              data-testid="service-mode-badge"
              className="mt-3 inline-flex items-center gap-2 px-3 py-2 font-mono text-xs"
              style={{ background: "rgba(34,197,94,0.08)", border: "1px solid #22c55e", color: "#22c55e" }}
            >
              🛠 SERVICE MODE
            </div>
          )}
          {calc.tdee.junior_capped && (
            <div className="mt-2 font-mono text-xs tracking-widest text-amber-400" data-testid="junior-cap-badge">
              ⚠ {t.dashboard.junior_capped}
            </div>
          )}
          {codeStatus && (
            <div className="mt-2 font-mono text-xs tracking-widest text-white/60" data-testid="plans-counter">
              {codeStatus.plans_remaining === -1 ? (
                <span className="text-green-400">∞ {t.dashboard.plans_unlimited}</span>
              ) : (
                <>
                  {t.dashboard.plans_remaining}:{" "}
                  <span className="text-white">{codeStatus.plans_remaining}</span>
                  <span className="text-white/40"> / {codeStatus.plans_total}</span>
                </>
              )}
            </div>
          )}
          {codeStatus && (codeStatus.plans_consumed > 0) && (
            <div
              data-testid="seats-widget"
              className="mt-3 inline-flex items-center gap-2 px-3 py-2 font-mono text-xs"
              style={{ background: "#0E0E10", border: "1px solid #27272A" }}
            >
              <span className="tracking-widest text-white/50">{t.dashboard.seats_title}:</span>
              <span className="text-white" data-testid="seats-pro-count">
                {codeStatus.plans_pro_count} {codeStatus.plans_pro_count === 1 ? t.dashboard.seats_athlete : t.dashboard.seats_athletes}
              </span>
              <span className="text-white/30">+</span>
              <span className="text-amber-300" data-testid="seats-lifestyle-count">
                {codeStatus.plans_lifestyle_count} {t.dashboard.seats_family}
              </span>
              {codeStatus.plans_remaining !== -1 && (
                <span className="text-white/40">
                  · {codeStatus.plans_consumed} {t.dashboard.seats_of} {codeStatus.plans_total}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={startNewPlan}
            data-testid="new-plan-btn"
            className="btn-secondary"
          >
            ↻ {t.dashboard.new_plan}
          </button>
          <button
            onClick={downloadPDF}
            disabled={pdfLoading}
            data-testid="download-pdf-btn"
            className="btn-primary"
          >
            {pdfLoading ? t.common.loading : `↓ ${t.dashboard.download_pdf}`}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6 overflow-x-auto" style={{ borderColor: "#27272A" }}>
        <div className="flex gap-1 min-w-max">
          {TABS.map((tk) => (
            <button
              key={tk}
              data-testid={`tab-${tk}`}
              onClick={() => setTab(tk)}
              className={`tab-btn ${tab === tk ? "active" : ""}`}
            >
              {t.dashboard.tabs[tk]}
            </button>
          ))}
        </div>
      </div>

      <div className="animate-fade-up">
        {tab === "overview" && <Overview profile={profile} calc={calc} weeklyData={weeklyData} />}
        {tab === "daily" && <DailyPlan profile={profile} />}
        {tab === "weekly" && <WeeklyPlan profile={profile} weeklyData={weeklyData} onDayTypeChange={handleDayTypeChange} />}
        {tab === "supplements" && <Supplements profile={profile} />}
        {tab === "vitamins" && <Vitamins profile={profile} />}
        {tab === "checkin" && <Checkin profile={profile} />}
        {tab === "cravings" && <Cravings profile={profile} />}
        {tab === "foodlog" && <FoodLog profile={profile} />}
      </div>
    </div>
  );
};

export default Dashboard;
